import React, { createContext, useState, useEffect, useContext } from 'react';
import { getMessageTemplates } from '../services/templatesApi';
import defaultTemplatesConfig from '../config/messageTemplatesConfig';
import { initializeTemplates, validateTemplates, mergeWithDefaultTemplates } from '../utils/templateUtils';
import { resolveStoreLineService } from '../utils/storeDocumentHeader';
import { useAuth } from './AuthContext';

// Create context
const TemplatesContext = createContext();

// Create provider component
export const TemplatesProvider = ({ children }) => {
  const [templates, setTemplates] = useState(defaultTemplatesConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  // Use the imported default templates
  const defaultTemplates = defaultTemplatesConfig;

  // Initialize and fetch templates when component mounts or user changes
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        // Initialize templates ensures we have valid data
        const data = await initializeTemplates();

        // BIGLI-35（PM Bridget 2026-09-04「目前門市資訊還是沒有顯示出來」）：
        // 這裡舊寫法是「validateTemplates 不過 → setTemplates(defaultTemplates)」，
        // 也就是把伺服器那份**整包丟掉**。而正式站的 companyEmail 本來就是空字串
        // （公司資訊區塊已隱藏，沒有入口可填），舊的必填名單卻含 companyEmail
        // → 每次載入都判定失敗 → templates 變成 config/messageTemplatesConfig，
        // 那份預設值的 storeProfiles 是 []、也沒有 stores，於是門市抬頭（BIGLI-33）、
        // 門市 LOGO（BIGLI-34）、門市 Line 客服（BIGLI-35）與報價單的門市下拉
        // 全部一起消失。伺服器端的簽署頁 payload 是自己讀 DB 解的，所以只有
        // **登入後的畫面**中招，這就是 85f0713 修完簽署頁後 PM 仍然看不到門市資訊的原因。
        //
        // 現在一律以「預設值墊底、伺服器值覆蓋」合併，永遠不丟伺服器資料；
        // 驗證只降級成警告，用來提醒訊息模板欄位掉了，不再決定資料的生死。
        const merged = mergeWithDefaultTemplates(data);

        if (!validateTemplates(merged)) {
          console.warn('Templates 缺少部分訊息模板欄位，缺的欄位以預設值補齊（伺服器資料保留）');
        }

        setTemplates(merged);
        setError(null);
      } catch (err) {
        console.error('Failed to load templates:', err);
        setError('無法取得訊息模板，使用預設值');
        // Use default templates if fetch fails
        setTemplates(defaultTemplates);
      } finally {
        setLoading(false);
      }
    };

    // Reload templates when the user changes (different company)
    if (currentUser) {
      loadTemplates();
    } else {
      // BIGLI-50：未登入時不會有任何請求把 loading 從初值 true 翻回 false，
      // 它會永遠停在那裡——一旦 header 開始「等 templates 就緒才畫」（本票），
      // 公開簽署頁（SignaturePage / FullscreenPreviewModal）就會卡死在佔位上。
      // QuotationPreview 另有「有 templatesOverride → 立刻畫」那道保險，這裡是把
      // loading 的語意補正：它代表「請求進行中」，不代表「還沒拿到伺服器資料」。
      setLoading(false);
    }
  }, [currentUser?.company]);

  // Format company info HTML
  //
  // BIGLI-35：Line 客服連結改成「門市優先、對不到回退公司層級」（PM Bridget 2026-09-01
  // 「各門市都會有一個 Line 客服」）。呼叫端把該張單據的門市（quotation.store 字串快照）
  // 傳進來即可；沒有門市欄位的單據（請款單）不傳，就照舊拿公司層級那組。
  // 空字串時整行不印——舊版無條件印 <a href="">Line客服：</a>，會在單據上留一個點不動的空連結。
  const getCompanyInfoHtml = (store) => {
    const t = templates || defaultTemplates;
    const lineService = resolveStoreLineService(t, store);
    return `
      <p><strong>公司名稱：</strong>${t.companyName}</p>
      <p><strong>公司信箱：</strong>${t.companyEmail}</p>
      <p><strong>公司地址：</strong>${t.companyAddress}</p>
      <p><strong>統一編號：</strong>${t.companyTaxId}</p>
      <p><strong>專案聯絡人：</strong>${t.contactPerson}</p>
      ${lineService ? `<p><strong>Line客服：</strong><a href="${lineService}" target="_blank">${lineService}</a></p>` : ''}
      ${t.portfolioUrl ? `<p><strong>公司作品集：</strong><a href="${t.portfolioUrl}" target="_blank">${t.portfolioUrl}</a></p>` : ''}
    `;
  };

  // Format bank info HTML
  const getBankInfoHtml = (paymentMethod, paymentDetails) => {
    const t = templates || defaultTemplates;
    return `
      <p><strong>付款方式：</strong>${
        paymentMethod === 'bank_transfer' ? '銀行轉帳' :
        paymentMethod === 'credit_card' ? '信用卡' :
        paymentMethod === 'cash' ? '現金' : '其他'
      }</p>
      <p><strong>戶名：</strong>${t.accountName || ''}</p>
      <p><strong>銀行名稱：</strong>${t.bankName || ''}</p>
      <p><strong>分行名稱：</strong>${t.branchName || ''}</p>
      <p><strong>銀行代碼：</strong>${t.bankCode || ''}</p>
      <p><strong>分行代碼：</strong>${t.branchCode || ''}</p>
      <p><strong>銀行帳號：</strong>${t.bankAccount || ''}</p>
      ${paymentDetails ? `<p><strong>付款說明：</strong>${paymentDetails}</p>` : ''}
    `;
  };

  // Format status for display
  const getStatusDisplay = (status) => {
    const t = templates || defaultTemplates;
    switch (status) {
      case 'pending':
        return t.statusPending;
      case 'partial':
        return t.statusPartial;
      case 'paid':
        return t.statusPaid;
      case 'overdue':
        return t.statusOverdue;
      default:
        return status;
    }
  };

  // Refresh templates from server
  const refreshTemplates = async () => {
    try {
      const data = await getMessageTemplates();
      if (data && Object.keys(data).length > 0) {
        // BIGLI-35：與載入時同一套合併規則。公司設定頁存檔後會呼叫這支，
        // 若這裡直接整包取代，伺服器沒回的欄位會變 undefined 印在單據上。
        setTemplates(mergeWithDefaultTemplates(data));
      }
    } catch (err) {
      console.error('Failed to refresh templates:', err);
    }
  };

  return (
    <TemplatesContext.Provider value={{
      templates: templates || defaultTemplates,
      loading,
      error,
      getCompanyInfoHtml,
      getBankInfoHtml,
      getStatusDisplay,
      refreshTemplates
    }}>
      {children}
    </TemplatesContext.Provider>
  );
};

// Custom hook to use the templates context
export const useTemplates = () => {
  const context = useContext(TemplatesContext);
  if (context === undefined) {
    throw new Error('useTemplates must be used within a TemplatesProvider');
  }
  return context;
};

export default TemplatesContext;
